import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";
import { useModuleExercisesOptional } from "@/contexts/ModuleExercisesContext";
import type {
  EvaluateExerciseResponse,
  ExerciseItem,
} from "@/services/student/exercise.interfaces";

export type ExerciseVerifyStatus = "idle" | "verifying" | "verified" | "error";

interface StudentExerciseState {
  status: ExerciseVerifyStatus;
  evaluation: EvaluateExerciseResponse | null;
  error: string | null;
  answer: unknown;
  canVerify: boolean;
}

enum ActionType {
  SET_ANSWER = "SET_ANSWER",
  SET_CAN_VERIFY = "SET_CAN_VERIFY",
  VERIFY_REQUESTED = "VERIFY_REQUESTED",
  VERIFY_OK = "VERIFY_OK",
  VERIFY_FAIL = "VERIFY_FAIL",
}

type StudentExerciseAction =
  | { type: ActionType.SET_ANSWER; payload: unknown }
  | { type: ActionType.SET_CAN_VERIFY; payload: boolean }
  | { type: ActionType.VERIFY_REQUESTED }
  | { type: ActionType.VERIFY_OK; payload: EvaluateExerciseResponse }
  | { type: ActionType.VERIFY_FAIL; payload: string };

const initialState: StudentExerciseState = {
  status: "idle",
  evaluation: null,
  error: null,
  answer: undefined,
  canVerify: true,
};

const studentExerciseReducer = (
  state: StudentExerciseState,
  action: StudentExerciseAction
): StudentExerciseState => {
  switch (action.type) {
    case ActionType.SET_ANSWER:
      return {
        ...state,
        answer: action.payload,
        status: state.status === "verified" ? "idle" : state.status,
      };
    case ActionType.SET_CAN_VERIFY:
      return { ...state, canVerify: action.payload };
    case ActionType.VERIFY_REQUESTED:
      return { ...state, status: "verifying", error: null };
    case ActionType.VERIFY_OK:
      return {
        ...state,
        status: "verified",
        evaluation: action.payload,
        error: null,
      };
    case ActionType.VERIFY_FAIL:
      return { ...state, status: "error", error: action.payload };
    default:
      return state;
  }
};

export interface StudentExerciseContextType {
  item: Pick<ExerciseItem, "id" | "type"> & { moduleId?: string };
  moduleId: string;
  status: ExerciseVerifyStatus;
  evaluation: EvaluateExerciseResponse | null;
  error: string | null;
  answer: unknown;
  canVerify: boolean;
  setAnswer: (answer: unknown) => void;
  setCanVerify: (canVerify: boolean) => void;
  verify: (answer?: unknown) => Promise<EvaluateExerciseResponse>;
  registerVerifyHandler: (handler: () => void | Promise<void>) => void;
  runRegisteredVerify: () => void | Promise<void>;
}

const StudentExerciseContext = createContext<
  StudentExerciseContextType | undefined
>(undefined);

type StudentExerciseProviderProps = {
  item: Pick<ExerciseItem, "id" | "type"> & { moduleId?: string };
  moduleId?: string;
  children: ReactNode;
};

export const StudentExerciseProvider: React.FC<
  StudentExerciseProviderProps
> = ({ item, moduleId: moduleIdProp, children }) => {
  const moduleExercises = useModuleExercisesOptional();
  const moduleId =
    moduleIdProp || moduleExercises?.moduleId || item.moduleId || "";
  const [state, dispatch] = useReducer(studentExerciseReducer, initialState);
  const verifyHandlerRef = useRef<(() => void | Promise<void>) | null>(null);
  const answerRef = useRef<unknown>(undefined);
  answerRef.current = state.answer;

  const setAnswer = useCallback((answer: unknown) => {
    dispatch({ type: ActionType.SET_ANSWER, payload: answer });
  }, []);

  const setCanVerify = useCallback((canVerify: boolean) => {
    dispatch({ type: ActionType.SET_CAN_VERIFY, payload: canVerify });
  }, []);

  const registerVerifyHandler = useCallback(
    (handler: () => void | Promise<void>) => {
      verifyHandlerRef.current = handler;
    },
    []
  );

  const runRegisteredVerify = useCallback(() => {
    if (verifyHandlerRef.current) {
      return verifyHandlerRef.current();
    }
    return undefined;
  }, []);

  const verify = useCallback(
    async (answer?: unknown): Promise<EvaluateExerciseResponse> => {
      const payload = answer !== undefined ? answer : answerRef.current;
      dispatch({ type: ActionType.VERIFY_REQUESTED });
      try {
        if (!moduleExercises?.evaluateExercise) {
          throw new Error(
            "Verificação do módulo indisponível neste contexto."
          );
        }
        const evaluation = await moduleExercises.evaluateExercise(
          String(item.id),
          payload,
          item.type,
          moduleId || undefined
        );
        dispatch({ type: ActionType.VERIFY_OK, payload: evaluation });
        return evaluation;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "Ocorreu um erro ao verificar sua resposta. Tente novamente.";
        dispatch({ type: ActionType.VERIFY_FAIL, payload: message });
        throw err;
      }
    },
    [moduleExercises, item.id, item.type, moduleId]
  );

  const value = useMemo(
    (): StudentExerciseContextType => ({
      item,
      moduleId,
      status: state.status,
      evaluation: state.evaluation,
      error: state.error,
      answer: state.answer,
      canVerify: state.canVerify,
      setAnswer,
      setCanVerify,
      verify,
      registerVerifyHandler,
      runRegisteredVerify,
    }),
    [
      item,
      moduleId,
      state.status,
      state.evaluation,
      state.error,
      state.answer,
      state.canVerify,
      setAnswer,
      setCanVerify,
      verify,
      registerVerifyHandler,
      runRegisteredVerify,
    ]
  );

  return (
    <StudentExerciseContext.Provider value={value}>
      {children}
    </StudentExerciseContext.Provider>
  );
};

export const useExercise = (): StudentExerciseContextType => {
  const context = useContext(StudentExerciseContext);
  if (!context) {
    throw new Error("useExercise deve ser usado dentro de StudentExerciseProvider");
  }
  return context;
};

export const useExerciseOptional = (): StudentExerciseContextType | undefined =>
  useContext(StudentExerciseContext);
